import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { supabase } from "../lib/supabase";
import { trackSquadCreated } from "../lib/analytics";
import { useAuth } from "./AuthContext";
import type { Database } from "../lib/database.types";
import type { PostgrestError } from "@supabase/supabase-js";

type SquadRow = Database["public"]["Tables"]["squads"]["Row"];
type SquadMemberRow = Database["public"]["Tables"]["squad_members"]["Row"];

export type SquadMode = "global" | "squad";

interface Squad {
  id: string;
  name: string;
  code: string;
  owner_id: string;
  member_count?: number;
}

interface SquadContextType {
  mode: SquadMode;
  currentSquad: Squad | null;
  loading: boolean;
  switchToGlobal: () => void;
  switchToSquad: () => void;
  createSquad: (
    name: string
  ) => Promise<{
    data: Squad | null;
    error: PostgrestError | { message: string } | null;
  }>;
  joinSquad: (
    code: string
  ) => Promise<{
    data: Squad | null;
    error: PostgrestError | { message: string } | null;
  }>;
  leaveSquad: () => Promise<{
    error: PostgrestError | { message: string } | null;
  }>;
  refetch: () => Promise<void>;
}

const SquadContext = createContext<SquadContextType | undefined>(undefined);

interface SquadProviderProps {
  children: ReactNode;
}

export function SquadProvider({ children }: SquadProviderProps) {
  const { user, profile } = useAuth();
  const [mode, setMode] = useState<SquadMode>("global");
  const [currentSquad, setCurrentSquad] = useState<Squad | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserSquad = useCallback(async () => {
    if (!user) {
      setCurrentSquad(null);
      setMode("global");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Check if user is in a squad
      const { data: memberData, error: memberError } = await supabase
        .from("squad_members")
        .select("squad_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (memberError) throw memberError;

      if (memberData) {
        // Fetch squad details
        const { data: squadData, error: squadError } = await supabase
          .from("squads")
          .select("*")
          .eq("id", memberData.squad_id)
          .single();

        if (squadError) throw squadError;

        // Get member count
        const { count } = await supabase
          .from("squad_members")
          .select("*", { count: "exact", head: true })
          .eq("squad_id", memberData.squad_id);

        const squad: Squad = {
          id: squadData.id,
          name: squadData.name,
          code: squadData.code,
          owner_id: squadData.owner_id,
          member_count: count || 0,
        };

        setCurrentSquad(squad);

        // Set mode based on profile's current_squad_id
        if (profile?.current_squad_id === squadData.id) {
          setMode("squad");
        } else {
          setMode("global");
        }
      } else {
        setCurrentSquad(null);
        setMode("global");
      }
    } catch (error) {
      setCurrentSquad(null);
      setMode("global");
    } finally {
      setLoading(false);
    }
  }, [user, profile]);

  useEffect(() => {
    fetchUserSquad();
  }, [fetchUserSquad]);

  const switchToGlobal = useCallback(async () => {
    if (!user) return;

    setMode("global");

    // Update profile's current_squad_id to null
    await supabase
      .from("profiles")
      .update({ current_squad_id: null })
      .eq("id", user.id);
  }, [user]);

  const switchToSquad = useCallback(async () => {
    if (!user || !currentSquad) return;

    setMode("squad");

    // Update profile's current_squad_id
    await supabase
      .from("profiles")
      .update({ current_squad_id: currentSquad.id })
      .eq("id", user.id);
  }, [user, currentSquad]);

  const createSquad = useCallback(
    async (name: string) => {
      if (!user) {
        return { data: null, error: { message: "User not authenticated" } };
      }

      try {
        // Check if user already in a squad
        const { data: existingMember, error: checkError } = await supabase
          .from("squad_members")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (checkError) {
        }

        if (existingMember) {
          return {
            data: null,
            error: {
              message:
                "You are already in a squad. Leave your current squad first.",
            },
          };
        }

        // Create squad
        const { data: squadData, error: squadError } = await supabase
          .from("squads")
          .insert({ name, owner_id: user.id })
          .select()
          .single();

        if (squadError) {
          return { data: null, error: squadError };
        }

        // Join the squad automatically
        const { error: memberError } = await supabase
          .from("squad_members")
          .insert({ squad_id: squadData.id, user_id: user.id });

        if (memberError) {
          // Check if it's a duplicate key error
          if (memberError.code === "23505") {
            await supabase.from("squads").delete().eq("id", squadData.id);
            return {
              data: null,
              error: {
                message:
                  "You are already in a squad. Leave your current squad first.",
              },
            };
          }
          // Rollback: delete the squad
          await supabase.from("squads").delete().eq("id", squadData.id);
          return { data: null, error: memberError };
        }

        // Set as current squad
        await supabase
          .from("profiles")
          .update({ current_squad_id: squadData.id })
          .eq("id", user.id);

        const squad: Squad = {
          id: squadData.id,
          name: squadData.name,
          code: squadData.code,
          owner_id: squadData.owner_id,
          member_count: 1,
        };

        setCurrentSquad(squad);
        setMode("squad");

        // Track squad creation
        trackSquadCreated(squad.name);

        return { data: squad, error: null };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create squad";
        return { data: null, error: { message: errorMessage } };
      }
    },
    [user]
  );

  const joinSquad = useCallback(
    async (code: string) => {
      if (!user) {
        return { data: null, error: { message: "User not authenticated" } };
      }

      try {
        // Check if user already in a squad
        const { data: existingMember } = await supabase
          .from("squad_members")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (existingMember) {
          return {
            data: null,
            error: {
              message:
                "You are already in a squad. Leave your current squad first.",
            },
          };
        }

        // Find squad by code
        const { data: squadData, error: squadError } = await supabase
          .from("squads")
          .select("*")
          .eq("code", code.toUpperCase())
          .maybeSingle();

        if (squadError) {
          return { data: null, error: squadError };
        }

        if (!squadData) {
          return { data: null, error: { message: "Squad not found" } };
        }

        // Join the squad
        const { error: memberError } = await supabase
          .from("squad_members")
          .insert({ squad_id: squadData.id, user_id: user.id });

        if (memberError) {
          // Check if it's a duplicate key error
          if (memberError.code === "23505") {
            return {
              data: null,
              error: {
                message:
                  "You are already in a squad. Leave your current squad first.",
              },
            };
          }
          return { data: null, error: memberError };
        }

        // Set as current squad
        await supabase
          .from("profiles")
          .update({ current_squad_id: squadData.id })
          .eq("id", user.id);

        const squad: Squad = {
          id: squadData.id,
          name: squadData.name,
          code: squadData.code,
          owner_id: squadData.owner_id,
        };

        setCurrentSquad(squad);
        setMode("squad");

        return { data: squad, error: null };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to join squad";
        return { data: null, error: { message: errorMessage } };
      }
    },
    [user]
  );

  const leaveSquad = useCallback(async () => {
    if (!user || !currentSquad) {
      return { error: { message: "No squad to leave" } };
    }

    // Remove from squad members
    const { error } = await supabase
      .from("squad_members")
      .delete()
      .eq("user_id", user.id);

    if (error) {
      return { error };
    }

    // Clear current squad in profile
    await supabase
      .from("profiles")
      .update({ current_squad_id: null })
      .eq("id", user.id);

    setCurrentSquad(null);
    setMode("global");

    return { error: null };
  }, [user, currentSquad]);

  const value = {
    mode,
    currentSquad,
    loading,
    switchToGlobal,
    switchToSquad,
    createSquad,
    joinSquad,
    leaveSquad,
    refetch: fetchUserSquad,
  };

  return (
    <SquadContext.Provider value={value}>{children}</SquadContext.Provider>
  );
}

export function useSquad() {
  const context = useContext(SquadContext);
  if (context === undefined) {
    throw new Error("useSquad must be used within a SquadProvider");
  }
  return context;
}
